import { QueryClient } from '@tanstack/react-query'
import { ApiError } from './api-client'

/**
 * Shared server-state cache.
 *
 * Retrying a 401, a 403 or a validation failure only delays the error the admin
 * needs to see, so retries are reserved for the failures that are actually
 * transient.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        retry: (failureCount, error) => {
          if (error instanceof ApiError) {
            if (error.status >= 400 && error.status < 500) return false
            return failureCount < 2
          }
          return failureCount < 2
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      },
      mutations: {
        // A write that failed must never be replayed automatically: the admin
        // decides whether to try again.
        retry: false,
      },
    },
  })
}

/**
 * Query keys in one place, so a mutation can invalidate exactly the lists its
 * write affected instead of blanking the whole cache.
 */
export const queryKeys = {
  dashboard: {
    all: ['dashboard'] as const,
    stats: (trendDays: number) => ['dashboard', 'stats', trendDays] as const,
    activity: (limit: number) => ['dashboard', 'activity', limit] as const,
  },
  reporting: {
    all: ['reporting'] as const,
    /** Keyed on the range preset, which is what decides the from/to window. */
    bookings: (range: string) => ['reporting', 'bookings', range] as const,
  },
  users: {
    all: ['users'] as const,
    list: (filters: unknown) => ['users', 'list', filters] as const,
  },
  drivers: {
    all: ['drivers'] as const,
    list: (filters: unknown) => ['drivers', 'list', filters] as const,
    eligible: ['drivers', 'eligible'] as const,
  },
  tripRequests: {
    all: ['trip-requests'] as const,
    list: (filters: unknown) => ['trip-requests', 'list', filters] as const,
    /**
     * Its own key because the answer depends on the clock: the refund band a
     * booking falls into changes as its pickup time approaches, so this is
     * re-read every time the dialog opens rather than served from the list.
     */
    cancellationOptions: (tripRequestId: number) =>
      ['trip-requests', 'cancellation-options', tripRequestId] as const,
  },
  /**
   * A booking's operator history, keyed on whichever id the screen has. Both
   * resolve to the same rows server-side; keeping them apart means the trips
   * page and the requests page cache independently instead of one evicting the
   * other's entry.
   */
  changeLog: {
    all: ['change-log'] as const,
    forTrip: (tripId: number) => ['change-log', 'trip', tripId] as const,
    forBooking: (tripRequestId: number) => ['change-log', 'booking', tripRequestId] as const,
  },
  penalties: {
    all: ['penalties'] as const,
    list: (filters: unknown) => ['penalties', 'list', filters] as const,
    detail: (id: number) => ['penalties', 'detail', id] as const,
    forCustomer: (userId: number) => ['penalties', 'customer', userId] as const,
  },
  /**
   * Customer requests to change a booking.
   *
   * `openCount` is its own key so the sidebar count refreshes when a decision
   * is made without dragging the whole list with it.
   */
  tripEdits: {
    all: ['trip-edits'] as const,
    list: (filters: unknown) => ['trip-edits', 'list', filters] as const,
    detail: (id: number) => ['trip-edits', 'detail', id] as const,
    forBooking: (tripRequestId: number) => ['trip-edits', 'booking', tripRequestId] as const,
    openCount: ['trip-edits', 'open-count'] as const,
  },
  trips: {
    all: ['trips'] as const,
    list: (filters: unknown) => ['trips', 'list', filters] as const,
    detail: (id: number) => ['trips', 'detail', id] as const,
  },
  /**
   * Road geometry for the route map.
   *
   * Keyed on the two endpoints rather than on a booking id: every booking along
   * the same corridor then shares one lookup, and moving a pickup produces a
   * different key instead of a cached line to the old address. Nothing here
   * comes from the Drive For Me API, so no mutation ever invalidates it — the
   * road between two fixed points does not change while a dialog is open.
   */
  routes: {
    all: ['routes'] as const,
    driving: (fromLat: number, fromLng: number, toLat: number, toLng: number) =>
      ['routes', 'driving', fromLat, fromLng, toLat, toLng] as const,
  },
  promos: {
    all: ['promos'] as const,
    list: (filters: unknown) => ['promos', 'list', filters] as const,
    detail: (id: number) => ['promos', 'detail', id] as const,
  },
  refunds: {
    all: ['refunds'] as const,
    list: (filters: unknown) => ['refunds', 'list', filters] as const,
    summary: (filters: unknown) => ['refunds', 'summary', filters] as const,
    detail: (id: number) => ['refunds', 'detail', id] as const,
    history: (id: number) => ['refunds', 'history', id] as const,
    admins: ['refunds', 'admins'] as const,
    /**
     * Eligibility is its own key so that issuing a refund can invalidate exactly
     * the booking it touched — the remaining balance changes, and a stale value
     * would let an operator try to refund money that has already gone back.
     */
    eligibility: (tripRequestId: number) => ['refunds', 'eligibility', tripRequestId] as const,
  },
  /**
   * Payment verification, proof and points — all keyed on the **trip request**
   * id rather than the trip id, because a trip has no payment of its own and
   * inherits the booking's.
   *
   * That is also why verifying a payment invalidates the trips list: the trip
   * row renders the same payment summary, and leaving it stale would show an
   * operator an unverified badge on a payment they had just approved.
   */
  payments: {
    all: ['payments'] as const,
    verification: (tripRequestId: number) => ['payments', 'verification', tripRequestId] as const,
    points: (tripRequestId: number) => ['payments', 'points', tripRequestId] as const,
    instapayConfig: ['payments', 'instapay-config'] as const,
  },
} as const
